import { useMemo, type ReactElement } from 'react'
import { filterArchived, isDefined, isNotDefined, isNotEmpty, messageFromError, type AppData } from '@terros/common'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Card, Group, SimpleGrid, Stack, Title } from '@mantine/core'
import { useConnectFeature } from '@hooks'
import { useConnectApps, useConnectInstalls, useProfile, useTranslation } from '@front/model'
import { NotReady, Page, TAlert } from '@components'
import { buildConnectInstallationEditorHref, buildConnectMarketplaceAppHref } from '../integration/util'
import { AppDescription, ConnectAppLogo, ConnectCreatedBy, ConnectVersionBadge } from '../integration/ui'
import { ConnectAccessGuard } from '../integration/access'
import { InstallationCard } from '../integration.install/InstallationCard'

function ConnectMarketplacePage(): ReactElement {
  const { t } = useTranslation()
  const { company } = useProfile()
  const appsState = useConnectApps()
  const installsState = useConnectInstalls()
  const { milestone } = useConnectFeature()

  const allApps = useMemo(() => filterArchived(appsState.data), [appsState.data])

  const apps = useMemo(
    () =>
      allApps.filter(
        (app) => isDefined(app.currentVersion) && (app.status === 'published' || app.companyId === company?.companyId)
      ),
    [allApps, company?.companyId]
  )

  const appsById = useMemo(() => new Map(allApps.map((app) => [app.appId, app] as const)), [allApps])

  const companyInstalls = useMemo(
    () =>
      filterArchived(installsState.data)
        .filter((install) => !company?.companyId || install.companyId === company.companyId)
        .filter((install) => appsById.has(install.appId)),
    [appsById, company?.companyId, installsState.data]
  )

  const installedAppIds = useMemo(() => new Set(companyInstalls.map((install) => install.appId)), [companyInstalls])

  const availableApps = useMemo(() => apps.filter((app) => !installedAppIds.has(app.appId)), [apps, installedAppIds])

  const isLoading = appsState.isLoading || installsState.isLoading
  const error = messageFromError(appsState.error ?? installsState.error ?? null)

  return (
    <ConnectAccessGuard>
      <Page>
        <Page.Heading
          isFetching={appsState.isFetching || installsState.isFetching}
          milestone={milestone}
          refetch={() => {
            void Promise.all([appsState.refetch(), installsState.refetch()])
          }}
          title={t('Marketplace')}
        />
        <Page.Content scrollArea>
          <Stack gap='xl'>
            {(isLoading || error || !appsState.data || !installsState.data) && (
              <NotReady
                data={appsState.data ?? installsState.data}
                error={error}
                id={t('marketplace')}
                isLoading={isLoading}
              />
            )}

            {appsState.data && installsState.data && (
              <Stack gap='xl'>
                {isNotEmpty(companyInstalls) && (
                  <Stack gap='md'>
                    <Title order={2}>{t('Installed Apps')}</Title>
                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing='md'>
                      {companyInstalls.map((install) => {
                        const app = appsById.get(install.appId)
                        if (isNotDefined(app)) return null
                        return (
                          <InstallationCard
                            app={app}
                            install={install}
                            key={install.installId}
                            to={buildConnectInstallationEditorHref(app.appId, install.installId)}
                          />
                        )
                      })}
                    </SimpleGrid>
                  </Stack>
                )}

                <Stack gap='md'>
                  <Title order={2}>{t('Available Apps')}</Title>
                  {isNotEmpty(availableApps) ? (
                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing='md'>
                      {availableApps.map((app) => (
                        <AvailableAppCard app={app} key={app.appId} />
                      ))}
                    </SimpleGrid>
                  ) : (
                    <TAlert variant='discovery' title={t('No apps available')}>
                      {t('There are no marketplace apps available yet.')}
                    </TAlert>
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Page.Content>
      </Page>
    </ConnectAccessGuard>
  )
}

function AvailableAppCard({ app }: { app: AppData }): ReactElement {
  return (
    <Card
      withBorder
      component={Link}
      to={buildConnectMarketplaceAppHref(app.appId)}
      className='transition-opacity duration-200 hover:opacity-50'
    >
      <Stack>
        <Group justify='space-between' wrap='wrap'>
          <Group>
            <ConnectAppLogo avatarBlurhash={app.avatarBlurhash} avatarUrl={app.avatarUrl} name={app.name} />
            <Title order={3}>{app.name}</Title>
          </Group>
          <ConnectVersionBadge version={app.currentVersion} />
        </Group>
        <ConnectCreatedBy company={app.company} />
        <AppDescription clampLines={4} description={app.description} preview />
      </Stack>
    </Card>
  )
}

export const Route = createFileRoute('/integration/marketplace/')({
  component: ConnectMarketplacePage,
})
